import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import { softwareAPI } from '../../services/api';
import { toast } from 'react-toastify';

const SoftwareManager = () => {
  const [software, setSoftware] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSoftware, setEditingSoftware] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    version: '',
    category: '',
    tags: '',
    systemRequirements: '',
    file: null
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchSoftware();
    fetchCategories();
  }, []);

  const fetchSoftware = async () => {
    try {
      const res = await softwareAPI.getAll({ limit: 100 });
      setSoftware(res.data.data);
    } catch (error) {
      console.error('Error fetching software:', error);
      toast.error('Error al cargar el software');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await softwareAPI.getCategories();
      setCategories(res.data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleOpenDialog = (softwareItem = null) => {
    if (softwareItem) {
      setEditingSoftware(softwareItem);
      setFormData({
        name: softwareItem.name,
        description: softwareItem.description,
        version: softwareItem.version,
        category: softwareItem.category,
        tags: softwareItem.tags?.join(', ') || '',
        systemRequirements: softwareItem.systemRequirements?.description ||
                           (Object.keys(softwareItem.systemRequirements || {}).length > 0
                             ? JSON.stringify(softwareItem.systemRequirements, null, 2)
                             : ''),
        file: null
      });
    } else {
      setEditingSoftware(null);
      setFormData({
        name: '',
        description: '',
        version: '',
        category: '',
        tags: '',
        systemRequirements: '',
        file: null
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSoftware(null);
    setFormData({
      name: '',
      description: '',
      version: '',
      category: '',
      tags: '',
      systemRequirements: '',
      file: null
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('description', formData.description);
      submitData.append('version', formData.version);
      submitData.append('category', formData.category);
      submitData.append('tags', formData.tags);
      submitData.append('systemRequirements', formData.systemRequirements);

      if (formData.file) {
        submitData.append('file', formData.file);
      }

      if (editingSoftware) {
        // Actualizar software existente (sin archivo)
        const updateData = {
          name: formData.name,
          description: formData.description,
          version: formData.version,
          category: formData.category,
          tags: formData.tags,
          systemRequirements: formData.systemRequirements
        };
        await softwareAPI.update(editingSoftware.id, updateData);
        toast.success('Software actualizado correctamente');
      } else {
        // Crear nuevo software
        if (!formData.file) {
          toast.error('Se requiere un archivo para nuevo software');
          return;
        }
        await softwareAPI.create(submitData);
        toast.success('Software creado correctamente');
      }

      handleCloseDialog();
      fetchSoftware();
    } catch (error) {
      console.error('Error saving software:', error);
      toast.error(error.response?.data?.message || 'Error al guardar el software');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar "${name}"?`)) {
      try {
        await softwareAPI.delete(id);
        toast.success('Software eliminado correctamente');
        fetchSoftware();
      } catch (error) {
        console.error('Error deleting software:', error);
        toast.error('Error al eliminar el software');
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Gestión de Software
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Agregar Software
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Versión</TableCell>
              <TableCell>Categoría</TableCell>
              <TableCell>Tamaño</TableCell>
              <TableCell>Descargas</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {software.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Typography variant="subtitle2">{item.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {item.description.substring(0, 50)}...
                  </Typography>
                </TableCell>
                <TableCell>{item.version}</TableCell>
                <TableCell>
                  <Chip 
                    label={categories.find(c => c.value === item.category)?.label || item.category}
                    size="small"
                  />
                </TableCell>
                <TableCell>{formatFileSize(item.fileSize)}</TableCell>
                <TableCell>{item.downloadCount}</TableCell>
                <TableCell>
                  <Chip 
                    label={item.isActive ? 'Activo' : 'Inactivo'}
                    color={item.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(item)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(item.id, item.name)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para crear/editar software */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingSoftware ? 'Editar Software' : 'Agregar Software'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Nombre"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Versión"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={3}
                  label="Descripción"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Categoría</InputLabel>
                  <Select
                    value={formData.category}
                    label="Categoría"
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Tags (separados por coma)"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  margin="normal"
                  placeholder="ej: utilidad, gratis, windows"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Requisitos del Sistema"
                  value={formData.systemRequirements}
                  onChange={(e) => setFormData({ ...formData, systemRequirements: e.target.value })}
                  margin="normal"
                  placeholder='Texto plano: "Windows 10, 4GB RAM" o JSON: {"os": "Windows", "ram": "4GB"}'
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  <input
                    accept=".exe,.zip,.rar,.7z,.tar,.gz,.deb,.rpm,.msi,.dmg,.pkg,.apk"
                    style={{ display: 'none' }}
                    id="file-upload"
                    type="file"
                    onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                  />
                  <label htmlFor="file-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadIcon />}
                      fullWidth
                    >
                      {formData.file ? formData.file.name : 
                       editingSoftware ? 'Cambiar Archivo (Opcional)' : 'Seleccionar Archivo *'}
                    </Button>
                  </label>
                  {!editingSoftware && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Formatos permitidos: .exe, .zip, .rar, .7z, .tar, .gz, .deb, .rpm, .msi, .dmg, .pkg, .apk
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={uploading}
              startIcon={uploading ? <CircularProgress size={20} /> : null}
            >
              {uploading ? 'Guardando...' : (editingSoftware ? 'Actualizar' : 'Crear')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default SoftwareManager;